import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Download, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DOWNLOAD_URL = `${BACKEND_URL}/api/download/guide`;
const LS_LEAD = "drak_pending_lead_id";

export default function PaymentSuccess() {
  const downloadTriggered = useRef(false);
  const [hasTriggered, setHasTriggered] = useState(false);

  const triggerDownload = () => {
    const a = document.createElement("a");
    a.href = DOWNLOAD_URL;
    a.setAttribute("download", "Back-Pain-Relief-Guide.pdf");
    document.body.appendChild(a);
    a.click();
    a.remove();
    setHasTriggered(true);
  };

  useEffect(() => {
    if (downloadTriggered.current) return;
    downloadTriggered.current = true;
    // Auto-trigger download immediately after landing on /success
    triggerDownload();
    toast.success("Your PDF download has started!");
    // Cleanup any pending lead reference
    localStorage.removeItem(LS_LEAD);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-[#1E3A8A] mb-6 text-sm font-medium transition-colors"
          data-testid="success-back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-3xl shadow-xl shadow-blue-900/5 border border-slate-100 overflow-hidden"
          data-testid="payment-success-card"
        >
          <div className="p-8 sm:p-10 text-center">
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 14 }}
              className="mx-auto w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center mb-6"
            >
              <CheckCircle2 className="w-12 h-12 text-emerald-600" />
            </motion.div>

            <h1
              className="text-3xl sm:text-4xl font-black text-[#1E3A8A] leading-tight"
              style={{ fontFamily: "Outfit" }}
              data-testid="status-ready"
            >
              Payment Successful!
            </h1>
            <p className="text-slate-600 mt-3 text-base">
              Your Back Pain Relief Guide is downloading automatically.
              <br />If it didn't start, tap the button below.
            </p>

            <Button
              onClick={triggerDownload}
              className="mt-7 h-14 px-8 rounded-full bg-[#F97316] hover:bg-[#EA580C] text-white font-bold text-base cta-glow transition-all hover:-translate-y-0.5"
              data-testid="manual-download-button"
            >
              <Download className="w-5 h-5 mr-2" />
              {hasTriggered ? "Download PDF Again" : "Download PDF"}
            </Button>

            <div className="mt-8 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-100">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span className="text-xs font-semibold text-emerald-800">
                Lifetime access · Save the PDF safely
              </span>
            </div>

            <p className="text-xs text-slate-500 mt-6">
              Need help? WhatsApp{" "}
              <a
                href="https://wa.me/918618168799"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 font-semibold"
              >
                +91 86181 68799
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export { LS_LEAD };
